import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { shiftRequestsAPI, shiftsAPI } from '../../api/api';
import './ShiftRequests.css';

const ShiftRequests = () => {
  const { user, isAdmin, isManager } = useAuth();
  const canManageRequests = isAdmin() || isManager();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [collapsedCards, setCollapsedCards] = useState({});

  useEffect(() => {
    fetchRequests();
  }, [canManageRequests]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      let response;
      
      if (canManageRequests) {
        response = await shiftRequestsAPI.getAll();
      } else {
        response = await shiftRequestsAPI.getMyRequests();
      }
      
      setRequests(response.requests || response.shiftRequests || []);
      
      // Инициализируем все карточки как свернутые
      const initialCollapsedState = {};
      (response.requests || response.shiftRequests || []).forEach(request => {
        initialCollapsedState[request.id] = true;
      });
      setCollapsedCards(initialCollapsedState);
      
    } catch (error) {
      console.error('Ошибка загрузки запросов:', error);
      alert('Ошибка загрузки запросов: ' + error.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleCard = (requestId, event) => {
    // Проверяем, был ли клик по кнопке действия
    if (event.target.closest('.request-actions') || 
        event.target.closest('.btn') ||
        event.target.tagName === 'BUTTON' ||
        event.target.tagName === 'A') {
      return; // Не сворачиваем/разворачиваем если клик по кнопке
    }
    
    setCollapsedCards(prev => ({
      ...prev,
      [requestId]: !prev[requestId]
    }));
  };

  const handleDelete = async (requestId, event) => {
    if (event) {
      event.stopPropagation(); // Останавливаем всплытие события
    }
    
    if (!window.confirm('Вы уверены, что хотите удалить этот запрос? Комментарий в смене будет очищен.')) {
      return;
    }

    try {
   
      
      const request = requests.find(r => r.id === requestId);
      if (!request) {
        throw new Error('Запрос не найден');
      }

      await shiftRequestsAPI.delete(requestId);
      
      if (request.comment) {
        try {

          await shiftsAPI.update(request.shift_id, {
            comment: ''
          });

        } catch (shiftError) {

        }
      }

      alert('Запрос удален успешно');
      await fetchRequests();
      
    } catch (error) {
     
      alert('Ошибка при удалении запроса: ' + error.message);
    }
  };

  const handleCancelRequest = async (requestId, event) => {
    if (event) {
      event.stopPropagation(); // Останавливаем всплытие события
    }
    
    if (!window.confirm('Вы уверены, что хотите отменить этот запрос? Будет добавлен комментарий о отмене в смену.')) {
      return;
    }

    try {
      await shiftRequestsAPI.updateStatus(requestId, 'cancelled');
      alert('Запрос отменен');
      
      setTimeout(() => {
        fetchRequests();
      }, 1000);
      
    } catch (error) {
      alert('Ошибка при отмене запроса: ' + error.message);
    }
  };

  const handleApprove = async (requestId, event) => {
    if (event) {
      event.stopPropagation(); // Останавливаем всплытие события
    }
    
    if (!window.confirm('Вы уверены, что хотите одобрить этот запрос?')) {
      return;
    }

    try {
      await shiftRequestsAPI.updateStatus(requestId, 'approved');
      alert('Запрос одобрен');
      await fetchRequests();
    } catch (error) {
      alert('Ошибка при одобрении запроса: ' + error.message);
    }
  };

  const handleReject = async (requestId, event) => {
    if (event) {
      event.stopPropagation(); // Останавливаем всплытие события
    }
    
    if (!window.confirm('Вы уверены, что хотите отклонить этот запрос?')) {
      return;
    }

    try {
      await shiftRequestsAPI.updateStatus(requestId, 'rejected');
      alert('Запрос отклонен');
      await fetchRequests();
    } catch (error) {
      alert('Ошибка при отклонении запроса: ' + error.message);
    }
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString('ru-RU', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  const getShiftType = (startTime) => {
    const hour = new Date(startTime).getHours();
    return hour === 8 ? 'Дневная' : 'Ночная';
  };

  const filteredRequests = requests.filter(request => {
    if (filter === 'all') return true;
    return request.status === filter;
  });

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'На рассмотрении';
      case 'approved': return 'Одобрено';
      case 'rejected': return 'Отклонено';
      case 'cancelled': return 'Отменено';
      default: return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const canManageRequest = (request) => {
    return canManageRequests;
  };

  const canCancelRequest = (request) => {
    if (canManageRequests) return true;
    return request.from_user_id === user?.id;
  };

  if (loading) {
    return (
      <div className="shift-requests">
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i>
          Загрузка запросов...
        </div>
      </div>
    );
  }

  return (
    <div className="shift-requests">
      {/* Фильтры */}
      <div className="filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Все ({requests.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          На рассмотрении ({requests.filter(r => r.status === 'pending').length})
        </button>
        <button 
          className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
          onClick={() => setFilter('approved')}
        >
          Одобренные ({requests.filter(r => r.status === 'approved').length})
        </button>
        <button 
          className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          Отклоненные ({requests.filter(r => r.status === 'rejected').length})
        </button>
      </div>

      {/* Список запросов */}
      <div className="requests-list">
        {filteredRequests.length > 0 ? (
          filteredRequests.map(request => {
            const shiftDateTime = formatDateTime(request.shift_start_time);
            const shiftType = getShiftType(request.shift_start_time);
            const isCollapsed = collapsedCards[request.id];
            
            return (
              <div 
                key={request.id} 
                className={`request-card ${isCollapsed ? 'collapsed' : ''}`}
                onClick={(e) => toggleCard(request.id, e)}
              >
                

                <div className="request-header">
                  <div className="request-info">
                    <div className="users-exchange">
                      <span className="from-user">
                        {request.from_user_name} 
                        <i className="fas fa-arrow-right"></i>
                      </span>
                      <span className="to-user">{request.to_user_name}</span>
                    </div>
                    <div className="request-date">
                      Создан: {new Date(request.created_at).toLocaleDateString('ru-RU')}
                      {request.approver_user_name && (
                        <span className="approver-info">
                          • Согласовал: {request.approver_user_name}
                        </span>
                      )}
                    </div>
                    
                    {/* Компактная информация в свернутом состоянии */}
                    {isCollapsed && (
                      <div className="compact-info">
                        <div className="compact-shift-info">
                          <i className="fas fa-calendar"></i>
                          {shiftDateTime.date}
                        </div>
                        <div className="compact-shift-info">
                          <i className="fas fa-clock"></i>
                          {shiftDateTime.time} • {shiftType} смена
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={`status-badge ${getStatusClass(request.status)}`}>
                    {getStatusText(request.status)}
                     </div>
                {/* Индикатор свертывания */}
                <div className="toggle-indicator">
                  <i className="fas fa-chevron-up"></i>
                </div>
                  </div>
               

                {/* Содержимое, которое скрывается */}
                {!isCollapsed && (
                  <>
                    <div className="shifts-comparison">
                      <div className="shift-info">
                        <h4>Смена для передачи:</h4>
                        <div className="shift-details">
                          <div className="shift-date">{shiftDateTime.date}</div>
                          <div className="shift-time">{shiftDateTime.time}</div>
                          <div className="shift-type">{shiftType} смена</div>
                        </div>
                      </div>

                      <div className="exchange-arrow">
                        <i className="fas fa-exchange-alt"></i>
                      </div>

                      <div className="request-details">
                        <h4>Информация о запросе:</h4>
                        <div className="details-list">
                          <div className="detail-item">
                            <strong>От:</strong> {request.from_user_name}
                            {request.from_user_position && ` (${request.from_user_position})`}
                          </div>
                          <div className="detail-item">
                            <strong>Кому:</strong> {request.to_user_name}
                            {request.to_user_position && ` (${request.to_user_position})`}
                          </div>
                          {request.comment && (
                            <div className="detail-item">
                              <strong>Комментарий: </strong> 
                              <span>{(request.shift_comment || '').replace(/<[^>]*>/g, ' ')}</span>
                            </div>
                            
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Комментарий о передаче смены */}
                      {request.comment && (
                          <div className="transfer-comment">
                            <i className="fas fa-exchange-alt"></i>
                            <strong>Результат передачи:</strong>  
                            <span>{(request.comment || '').replace(/<[^>]*>/g, '')}</span>
                          </div>
                        )}

                    <div className="request-actions" onClick={(e) => e.stopPropagation()}>
                      {request.status === 'pending' && (
                        <>
                          {canManageRequest(request) && (
                            <>
                              <button 
                                className="exchange-btn"
                                onClick={(e) => handleApprove(request.id, e)}
                              >
                                <i className="fas fa-check"></i>
                                Одобрить
                              </button>
                              <button 
                                className="btn-delete"
                                onClick={(e) => handleReject(request.id, e)}
                              >
                                <i className="fas fa-times"></i>
                                Отклонить
                              </button>
                            </>
                          
                          )}
                        </>
                      )}
                      
                      
                    </div>
                  </>
                )}
              </div>
            );
          })
        ) : (
          <div className="no-requests">
            <i className="fas fa-exchange-alt"></i>
            <h3>Запросов не найдено</h3>
            <p>Нет запросов на обмен сменами по выбранному фильтру</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftRequests;
